import React, { useEffect, useState } from "react";

import { allCountries } from "country-region-data";
import { useTimezoneSelect } from "react-timezone-select";

import { gql } from "@apollo/client";
import { GenericTooltip } from "@components/tooltip-container";
import { H1, LightSwitch } from "@components/utilities";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type Profile as ProfileQuery,
  type UpdateUserDetails,
  type UpdateUserDetailsVariables,
} from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";
import { Select, SelectWithGroupedOptions } from "@shared/selects";
import TextBoxWithCopyButton from "@shared/text-box-with-copy-button";
import { TextInput } from "@shared/text-input";
import { verifyDomain } from "@utils/domain-verification";

import { UPDATE_USER_DETAILS } from "./queries";

interface ProfileProps {
  afterSuccessCallback?: () => void;
  isOnboardingFlow?: boolean;
}

/* TODO: Refactor/parse content out of component and move to a more common locations for use outside
   of settings, */
export function Profile({
  afterSuccessCallback,
  isOnboardingFlow,
}: ProfileProps) {
  const { loading, data } = useNorthbeamQuery<ProfileQuery>(PROFILE_QUERY);

  const [updateUserDetails, { loading: updateUserDetailsLoading }] =
    useNorthbeamMutation<UpdateUserDetails, UpdateUserDetailsVariables>(
      UPDATE_USER_DETAILS,
      { refetchQueries: [{ query: PROFILE_QUERY }] },
    );

  const COMMON_COUNTRIES = ["US"];

  const [companyName, setCompanyName] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [region, setRegion] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [domainName, setDomainName] = useState<string>("");
  const [canChangeDomain, setCanChangeDomain] = useState<boolean>(false);
  const [domainError, setDomainError] = useState<string>("");
  const [timezone, setTimezone] = useState<string>("");

  useEffect(() => {
    const userData = data?.me;
    if (userData) {
      setCompanyName(userData.companyName || "");
      setFirstName(userData.firstName || "");
      setLastName(userData.lastName || "");
      setDomainName(userData.domainName || "");
      setTimezone(userData.timezone || "");
      setCountry(userData.country || "");
      setRegion(userData.region || "");
      setPostalCode(userData.postalCode || "");

      if (!!userData.domainName && !verifyDomain(userData.domainName)) {
        setCanChangeDomain(false);
      } else {
        setCanChangeDomain(true);
      }
    }
  }, [data]);

  const onSubmitClicked = React.useCallback(async () => {
    const error = verifyDomain(domainName);
    if (error) {
      setDomainError(error);
    } else {
      setDomainError("");
      const result = await updateUserDetails({
        variables: {
          companyName,
          firstName,
          lastName,
          domainName,
          timezone,
          country,
          region,
          postalCode,
        },
      });

      setCanChangeDomain(false);

      setDomainName(result.data?.updateUserDetails.domainName || domainName);

      afterSuccessCallback && afterSuccessCallback();
    }
  }, [
    companyName,
    firstName,
    lastName,
    domainName,
    timezone,
    country,
    region,
    postalCode,
    updateUserDetails,
    afterSuccessCallback,
  ]);

  const handlePostalCodeChange = setPostalCode;

  const { options } = useTimezoneSelect({});
  const userId = data?.me.id;
  return (
    <>
      {!isOnboardingFlow && (
        <>
          <H1>Profile</H1>
          <hr />
        </>
      )}
      {loading && (
        <div className="row col-8">
          <div className="py-2 my-2">Please wait...</div>
        </div>
      )}
      {!loading && (
        <div className="flex flex-col space-y-6 max-w-md">
          {userId && (
            <div className="flex items-center justify-center">
              <TextBoxWithCopyButton text={userId} label="Account ID" />
            </div>
          )}
          <TextInput
            label="Company name"
            disabled={updateUserDetailsLoading}
            value={companyName}
            onChange={({ target }) => setCompanyName(target.value)}
          />
          <div className="flex space-x-4">
            <TextInput
              label="First name"
              disabled={updateUserDetailsLoading}
              value={firstName}
              onChange={({ target }) => setFirstName(target.value)}
            />
            <TextInput
              label="Last name"
              disabled={updateUserDetailsLoading}
              value={lastName}
              onChange={({ target }) => setLastName(target.value)}
            />
          </div>
          <div className="nb-input-container">
            <label className="label">Country</label>
            <SelectWithGroupedOptions
              onChange={setCountry}
              isSearchable
              isDisabled={updateUserDetailsLoading}
              groupedOptions={[
                {
                  options: allCountries
                    .filter(([, CountryCode]) =>
                      COMMON_COUNTRIES.includes(CountryCode),
                    )
                    .map(([CountryName]) => ({
                      label: CountryName,
                      value: CountryName,
                    })),
                },
                {
                  options: allCountries
                    .filter(
                      ([, CountryCode]) =>
                        !COMMON_COUNTRIES.includes(CountryCode),
                    )
                    .map(([CountryName]) => ({
                      label: CountryName,
                      value: CountryName,
                    })),
                },
              ]}
              value={country}
              className="text-base"
            />
          </div>
          <div className="flex space-x-4">
            <div className="nb-input-container">
              <label className="label">State or Region</label>
              <Select
                onChange={setRegion}
                isSearchable
                isDisabled={updateUserDetailsLoading || !country}
                options={allCountries
                  .filter(([CountryName]) => CountryName === country)
                  .flatMap(([, , Regions]) => Regions)
                  .map(([RegionName]) => ({
                    label: RegionName,
                    value: RegionName,
                  }))}
                value={region}
                className="text-base"
              />
            </div>
            <TextInput
              label="Postal code"
              disabled={updateUserDetailsLoading}
              value={postalCode}
              onChange={({ target }) => handlePostalCodeChange(target.value)}
              errorMessage={
                (!postalCode &&
                  !isOnboardingFlow &&
                  "please add a postal code") ||
                ""
              }
            />
          </div>
          <div className="nb-input-container">
            <label className="label">Timezone</label>
            <Select
              onChange={setTimezone}
              isDisabled={updateUserDetailsLoading}
              isSearchable
              options={options}
              value={timezone}
              className="text-base"
            />
          </div>
          {!isOnboardingFlow && (
            <>
              <div className="control-label">
                <GenericTooltip
                  content={
                    "Your domain will look something like northbeam.io, " +
                    "google.com, or bbc.co.uk"
                  }
                >
                  Domain name
                </GenericTooltip>
              </div>
              <div className="input-group flex-nowrap">
                <div className="input-group-prepend h-8">
                  <div className="input-group-text rounded-tr-none rounded-br-none rounded-md">
                    https://
                  </div>
                </div>
                <TextInput
                  inputClassName="rounded-tl-none rounded-bl-none"
                  disabled={!canChangeDomain || updateUserDetailsLoading}
                  value={domainName}
                  onChange={({ target }) =>
                    setDomainName(target.value.toLowerCase())
                  }
                  errorMessage={domainError}
                />
              </div>
            </>
          )}
          <div>
            <PrimaryButton
              size="md"
              disabled={
                updateUserDetailsLoading ||
                !timezone ||
                !domainName ||
                !firstName ||
                !lastName ||
                !companyName ||
                !postalCode
              }
              tabIndex={70}
              onClick={onSubmitClicked}
            >
              {isOnboardingFlow ? (
                <>
                  Continue <i className="fa-regular fa-sharp fa-arrow-right" />
                </>
              ) : (
                "SUBMIT"
              )}
            </PrimaryButton>
          </div>
        </div>
      )}
    </>
  );
}

export const PROFILE_QUERY = gql`
  query Profile {
    me {
      id
      firstName
      lastName
      companyName
      domainName
      timezone
      country
      region
      postalCode
    }
  }
`;
